import { Link } from '@mui/material';
import {
	DataGrid,
	GridCallbackDetails,
	GridColDef,
	GridPaginationModel,
	GridRenderCellParams,
} from '@mui/x-data-grid';
import { useInfiniteQuery } from '@tanstack/react-query';
import React from 'react';
import { Link as LinkRouter } from 'react-router-dom';
import { Workflow } from '../../api/ConfigApi';
import PolicyholderApi from '../../api/PolicyholderApi';
import { useAuthContext } from '../../context/auth';
import { ConfigData, useConfigContext } from '../../context/config';
import {
	extractStringsBetweenCurlyBraces,
	flattenPolicyholderFields,
} from '../../util/util';
import ProtectedComponent from '../ProtectedComponent';

const ROWS_PER_PAGE = 50;

type Field = {
	key: string;
	value: string;
};

type Policyholder = {
	locator: string;
	fields: Field[];
};

type Data = {
	id: string;
	locator: string;
	[key: string]: string;
};

const usePaginatedPolicyholders = (
	queryKey: string[],
	hasPermission: boolean,
	configData: ConfigData,
	token?: string,
) => {
	return useInfiniteQuery(
		queryKey,
		async ({ pageParam = '' }) => {
			const response = await PolicyholderApi.getPolicyholders(
				token,
				pageParam,
				ROWS_PER_PAGE,
			);

			const policyholderCardDefinition =
				configData.policyholderCardConfig?.workflows?.find(
					(w: Workflow) => w.message === 'policyHolderSearch.json',
				)?.value;

			const policyholderFields = flattenPolicyholderFields(
				configData.policyholderCreateFields,
			);
			const columnKeys = extractStringsBetweenCurlyBraces(
				policyholderCardDefinition?.title +
					policyholderCardDefinition?.subtitle,
			);

			const newPolicyholders = response.data.policyholders.map((p) => {
				const fields = Object.keys(p.entity.values).map((key) => {
					const value = p.entity.values[key].value[0];
					return {
						key,
						value,
					} as Field;
				});

				return {
					locator: p.locator,
					fields: fields.filter((f) => columnKeys.includes(f.key)),
				} as Policyholder;
			});

			let rows = [] as Data[];

			newPolicyholders.forEach((p) => {
				const row = { id: p.locator, locator: p.locator } as Data;
				p.fields.forEach((f) => {
					row[f.key] = f.value;
				});
				rows.push(row);
			});

			const columns = columnKeys.map((ck, index) => {
				const column = {
					field: ck,
					headerName: policyholderFields.find((f) => f.uri === ck)?.title,
					width: 200,
					renderCell: index === 0 ? ActiveRolesRow : undefined,
				} as GridColDef;
				return column;
			});

			columns.push({
				field: 'locator',
				headerName: 'Locator',
				width: 300,
				renderCell: !columnKeys?.length ? ActiveRolesRow : undefined,
			});

			return {
				columns,
				rows,
				pagingToken: response.data.pagingToken,
			};
		},
		{
			getPreviousPageParam: (firstPage) =>
				firstPage.pagingToken === '' ? undefined : firstPage.pagingToken,
			getNextPageParam: (lastPage) =>
				lastPage.pagingToken === '' ? undefined : lastPage.pagingToken,
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
			enabled: !!hasPermission,
		},
	);
};

const ActiveRolesRow = (params: GridRenderCellParams) => (
	<Link
		underline='hover'
		component={LinkRouter}
		to={`/policyholder/${params.row.locator}`}
	>
		{params.value}
	</Link>
);

interface Props {
	policyholderCount: string;
}

const PolicyholdersTable = ({ policyholderCount }: Props) => {
	const authData = useAuthContext();
	const configData = useConfigContext();
	const [page, setPage] = React.useState(0);
	const { authorizationToken } = authData.auth;
	const { hasPermission } = authData;
	const { data, isLoading, fetchNextPage, isFetchingNextPage } =
		usePaginatedPolicyholders(
			[
				'policyholders',
				authData.auth.authorizationToken,
				configData?.policyholderCardConfig,
			],
			hasPermission('policyholder.fetch'),
			configData,
			authorizationToken,
		);

	const handlePageChange = (
		model: GridPaginationModel,
		details: GridCallbackDetails<any>,
	) => {
		setPage(model.page);
		fetchNextPage();
	};

	return (
		<div style={{ display: 'flex', height: '100%' }}>
			<div style={{ flexGrow: 1 }}>
				{hasPermission('policyholder.fetch') ? (
					<DataGrid
						sx={{ backgroundColor: 'white.lightest' }}
						loading={isLoading || isFetchingNextPage}
						pageSizeOptions={[ROWS_PER_PAGE]}
						paginationModel={{ page, pageSize: ROWS_PER_PAGE }}
						paginationMode='server'
						rowCount={parseInt(policyholderCount)}
						onPaginationModelChange={handlePageChange}
						columns={data?.pages[page]?.columns || []}
						rows={data?.pages[page]?.rows || []}
					/>
				) : (
					<ProtectedComponent permission='policyholder.fetch' />
				)}
			</div>
		</div>
	);
};

export default PolicyholdersTable;
